<template>
  <div class="container-fluid search-page rfo-page rfo-page--standard oa-workspace">
    <!-- Header -->
    <div class="oa-page-toolbar">
      <router-link :to="{ name: 'pay-operations-order-amendments' }" class="oa-back-link">
        <i class="fa fa-arrow-left me-1"></i>Back to Queue
      </router-link>
      <h4 class="mb-0"><i class="fa fa-shield me-2"></i>Amendment Audit Log</h4>
    </div>

    <!-- Filters -->
    <div class="card card-absolute rfo-card rfo-card--primary oa-search-card">
      <div class="card-header bg-primary text-white">
        <h5 class="mb-0 text-white"><i class="fa fa-filter me-2"></i>Search Audit Entries</h5>
      </div>
      <div class="card-body border border-primary rounded-3">
        <div class="alert alert-light mb-3" role="alert">
          <i class="fa fa-shield font-primary me-2"></i>
          Find immutable amendment attempts by operator, action, order, or inclusive date range.
        </div>
        <div class="row g-3">
          <div class="col-md-3">
            <label class="form-label small mb-1">Operator</label>
            <div class="input-group input-group-sm">
              <button
                type="button"
                class="form-control border border-primary text-start oa-picker-trigger"
                :title="filters.operator ? `uid: ${filters.operator}` : 'Click to pick a ryd user'"
                @click="operatorModalOpen = true"
              >
                <span v-if="filters.operatorLabel" class="text-dark">{{
                  filters.operatorLabel
                }}</span>
                <span v-else-if="filters.operator" class="font-monospace text-muted small">{{
                  shortUuid(filters.operator)
                }}</span>
                <span v-else class="text-muted">Click to choose…</span>
              </button>
              <button
                v-if="filters.operator"
                type="button"
                class="btn btn-outline-secondary"
                aria-label="Clear operator"
                @click="clearOperator"
              >
                <i class="fa fa-times" />
              </button>
              <button
                v-else
                type="button"
                class="btn btn-outline-primary"
                aria-label="Pick operator"
                @click="operatorModalOpen = true"
              >
                <i class="fa fa-user" />
              </button>
            </div>
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Action</label>
            <select
              v-model="filters.action"
              class="form-select border border-primary form-select-sm"
            >
              <option value="">All</option>
              <option value="completeManually">Complete Manually</option>
              <option value="cancelManually">Cancel Manually</option>
              <option value="forceCompleteManually">Force Complete</option>
              <option value="refundAndCancelManually">Refund & Cancel</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">Order UUID</label>
            <input
              v-model="filters.orderUuid"
              type="text"
              class="form-control border border-primary form-control-sm"
              placeholder="UUID..."
            />
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">From Date (inclusive)</label>
            <input
              v-model="filters.fromDate"
              type="date"
              class="form-control border border-primary form-control-sm"
            />
          </div>
          <div class="col-md-2">
            <label class="form-label small mb-1">To Date (inclusive)</label>
            <input
              v-model="filters.toDate"
              type="date"
              class="form-control border border-primary form-control-sm"
            />
          </div>
          <div class="col-12 oa-action-row">
            <button
              class="btn btn-primary text-white"
              :disabled="isLoading"
              @click="searchFromStart"
            >
              <i class="fa fa-search me-1"></i>Search
            </button>
            <button class="btn btn-outline-secondary" :disabled="isLoading" @click="resetFilters">
              <i class="fa fa-undo me-1"></i>Reset
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="card card-absolute rfo-card rfo-table-card rfo-card--secondary oa-results-card">
      <div class="card-header bg-secondary text-white">
        <h5 class="mb-0 text-white">
          <span><i class="fa fa-history me-2"></i>Audit Entries</span>
          <span class="oa-card-header-meta ms-2">{{ totalItems }} total · page {{ page }}</span>
        </h5>
      </div>
      <div class="card-body border border-secondary rounded-3">
        <!-- Error -->
        <div v-if="error" class="alert alert-danger outline" role="alert">
          <i class="fa fa-exclamation-triangle me-2"></i>{{ error }}
        </div>

        <!-- Loading -->
        <div v-if="isLoading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        </div>

        <!-- Empty -->
        <div v-else-if="entries.length === 0 && !error" class="oa-empty-state">
          <i class="fa fa-search fa-3x text-muted mb-3"></i>
          <p class="text-muted">No audit entries found for the current filters.</p>
        </div>

        <!-- Table -->
        <div v-else class="table-responsive">
          <table class="table table-hover table-sm align-middle">
            <thead class="table-light">
              <tr>
                <th>Timestamp</th>
                <th>Operator</th>
                <th>Action</th>
                <th>Order UUID</th>
                <th>Result</th>
                <th>Reason</th>
                <th class="audit-action-col"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="entry in entries" :key="entry.attemptId || entry.timestamp">
                <td class="small text-nowrap">
                  <ZonedDateTime
                    :value="entry.executedAt || entry.timestamp"
                    :zone="userTimezone"
                    kind="user"
                  />
                </td>
                <td class="small">{{ entry.actor || '—' }}</td>
                <td>
                  <span class="badge bg-secondary">{{ entry.action }}</span>
                </td>
                <td class="font-monospace small" :title="entry.orderUuid">
                  {{ shortUuid(entry.orderUuid) }}
                </td>
                <td>
                  <span
                    class="badge"
                    :class="
                      entry.result === 'success'
                        ? 'bg-success'
                        : entry.result === 'partial'
                          ? 'bg-warning text-dark'
                          : 'bg-danger'
                    "
                  >
                    {{ entry.result }}
                  </span>
                </td>
                <td class="small text-muted">{{ entry.reason || '—' }}</td>
                <td>
                  <router-link
                    v-if="entry.orderUuid"
                    :to="{
                      name: 'pay-operations-order-amendment-detail',
                      params: { uuid: entry.orderUuid },
                    }"
                    class="btn btn-sm btn-outline-primary"
                  >
                    View
                  </router-link>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <PaginationFleet
          v-if="totalPages > 1"
          :current-page="page"
          :total-pages="totalPages"
          :is-loading="isLoading"
          class="mt-3"
          @page-change="goToPage"
        />
      </div>
    </div>

    <PayOpsUsageInstructions
      class="mt-4"
      description="Immutable record of every amendment attempt (success and failure). Use the filters above to narrow down by order, actor, or action."
      :required-roles="auditRequiredRoles"
      :workflow-notes="auditWorkflowNotes"
    />

    <RydUserAutocompleteModal
      :show="operatorModalOpen"
      title="Select Operator"
      @select="onOperatorSelected"
      @close="operatorModalOpen = false"
    />
  </div>
</template>

<script lang="ts">
import { ref, reactive, computed, onMounted } from 'vue';
import { apiClient, API_PATHS } from '@/services/api';
import { shortIdentifier as shortUuid } from '@/utils/display-formatters';
import PayOpsUsageInstructions from '../shared/PayOpsUsageInstructions.vue';
import RydUserAutocompleteModal from '../shared/RydUserAutocompleteModal.vue';
import PaginationFleet from '@/components/common/PaginationFleet.vue';
import ZonedDateTime from '@/components/common/ZonedDateTime.vue';
import { getUserAddDataFromLocalStorage } from '@/utils/userUtils';
import { resolveUserTimezone } from '@/utils/timezoneDisplay';
import {
  PAY_OPS_AUDIT_ROLES,
  buildAuditWorkflowNotes,
} from '@/services/ryd-pay-operations/order-amendments/usage-instructions-config';

const PAGE_SIZE = 50;

export default {
  name: 'OrderAmendmentsAudit',
  components: {
    PayOpsUsageInstructions,
    RydUserAutocompleteModal,
    PaginationFleet,
    ZonedDateTime,
  },
  setup() {
    const userTimezone = resolveUserTimezone((getUserAddDataFromLocalStorage() || {}).timezone);
    const entries = ref<any[]>([]);
    const totalItems = ref(0);
    const page = ref(1);
    const isLoading = ref(false);
    const error = ref('');
    const operatorModalOpen = ref(false);

    const filters = reactive({
      operator: '',
      operatorLabel: '',
      action: '',
      orderUuid: '',
      fromDate: '',
      toDate: '',
    });

    function onOperatorSelected(user: any) {
      filters.operator = user.uid;
      const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      filters.operatorLabel = name || user.email || user.uid;
      operatorModalOpen.value = false;
      page.value = 1;
      fetchAudit();
    }

    function clearOperator() {
      filters.operator = '';
      filters.operatorLabel = '';
      page.value = 1;
      fetchAudit();
    }

    const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / PAGE_SIZE)));

    async function fetchAudit() {
      isLoading.value = true;
      error.value = '';
      try {
        const params: Record<string, string | number> = {
          page: page.value,
          limit: PAGE_SIZE,
        };
        if (filters.operator) params.actor = filters.operator;
        if (filters.action) params.action = filters.action;
        if (filters.orderUuid) params.orderUuid = filters.orderUuid;
        if (filters.fromDate) params.fromDate = filters.fromDate;
        if (filters.toDate) params.toDate = filters.toDate;

        const { data } = await apiClient.rpIntegration.get(
          API_PATHS.rpIntegration.payOperations.orderAmendments.audit,
          { params },
        );
        entries.value = data?.attempts ?? data?.items ?? [];
        totalItems.value = data?.totalCount ?? data?.total ?? entries.value.length;
      } catch (err: any) {
        error.value = err?.response?.data?.message || err?.message || 'Failed to load audit log.';
      } finally {
        isLoading.value = false;
      }
    }

    function goToPage(p: number) {
      if (p < 1 || p > totalPages.value) return;
      page.value = p;
      fetchAudit();
    }

    function searchFromStart() {
      page.value = 1;
      fetchAudit();
    }

    function resetFilters() {
      filters.operator = '';
      filters.operatorLabel = '';
      filters.action = '';
      filters.orderUuid = '';
      filters.fromDate = '';
      filters.toDate = '';
      page.value = 1;
      fetchAudit();
    }

    onMounted(() => {
      fetchAudit();
    });

    return {
      entries,
      totalItems,
      page,
      isLoading,
      error,
      filters,
      totalPages,
      fetchAudit,
      goToPage,
      searchFromStart,
      resetFilters,
      shortUuid,
      userTimezone,
      operatorModalOpen,
      onOperatorSelected,
      clearOperator,
      auditRequiredRoles: PAY_OPS_AUDIT_ROLES,
      auditWorkflowNotes: buildAuditWorkflowNotes(),
    };
  },
};
</script>

<style scoped>
.audit-action-col {
  width: 80px;
}
.oa-picker-trigger {
  background-color: #fff;
  cursor: pointer;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.oa-picker-trigger:hover {
  background-color: rgba(0, 161, 254, 0.04);
}
</style>
